import TreeCache from '../services/cache/TreeCache';

/** Matches LogicBaseCollectionRow batch size — max app ids per /v1/apps request. */
export const COLLECTION_APPS_FETCH = 12;

export function getCollectionAppsPageMeta(appIds, loadedAppCount) {
  const totalApps = appIds?.length || 0;
  const initialFetch = Math.min(totalApps, COLLECTION_APPS_FETCH);
  const effectiveLimit = loadedAppCount != null ? loadedAppCount : initialFetch;
  return {
    totalApps,
    initialFetch,
    effectiveLimit,
    hasMore: totalApps > effectiveLimit,
  };
}

/** How many leading ids are already in TreeCache. Stops before the first miss so a fallback read does not start another fetch. */
function cachedPrefixCount(appIds, limit) {
  const max = Math.min(appIds.length, limit || 0);
  for (let i = 0; i < max; i += 1) {
    if (!TreeCache.trees[appIds[i]]) return i;
  }
  return max;
}

/**
 * Load one paginated window of collection apps via TreeCache.getByIds.
 * Returns isLoading true while the current window is still fetching.
 * While that fetch is in flight, keep the cached prefix on screen. Returning
 * null here unmounts the rail and the horizontal scroller remounts at 0.
 */
export function getCollectionAppsPage(appIds, loadedAppCount, lastLoadedLimit) {
  const meta = getCollectionAppsPageMeta(appIds, loadedAppCount);
  const { effectiveLimit } = meta;

  if (!appIds?.length) {
    return { ...meta, apps: [], isLoading: false, lastLoadedLimit: 0 };
  }

  let apps = TreeCache.getByIds(appIds.slice(0, effectiveLimit));
  if (!apps) {
    const prefixCount = cachedPrefixCount(appIds, lastLoadedLimit);
    if (prefixCount > 0) {
      apps = TreeCache.getByIds(appIds.slice(0, prefixCount));
      if (apps) {
        // Keep lastLoadedLimit unchanged while the expanded window is still fetching.
        return {
          ...meta,
          apps: apps.filter(({ _id }) => _id),
          isLoading: true,
          lastLoadedLimit,
        };
      }
    }
    return { ...meta, apps: null, isLoading: true, lastLoadedLimit: lastLoadedLimit || null };
  }

  return {
    ...meta,
    apps: apps.filter(({ _id }) => _id),
    isLoading: false,
    // Ids actually resolved. The home hero widens the window before the next
    // featured page exists; storing that future limit makes the next fallback
    // include uncached ids and the rail comes back empty.
    lastLoadedLimit: Math.min(effectiveLimit, appIds.length),
  };
}

export function nextCollectionAppsLoadedCount(loadedAppCount, initialFetch, totalApps) {
  const current = loadedAppCount != null ? loadedAppCount : initialFetch;
  return Math.min(totalApps, current + COLLECTION_APPS_FETCH);
}

/** Expand the paged window so `appId` is included, without shrinking a larger restore. */
export function loadedCountIncludingApp(appIds, appId, loadedAppCount) {
  const { initialFetch, totalApps } = getCollectionAppsPageMeta(appIds, loadedAppCount);
  const base = loadedAppCount != null ? loadedAppCount : initialFetch;
  if (!appId || !appIds?.length) return base;
  const index = appIds.findIndex((id) => String(id) === String(appId));
  if (index < 0) return base;
  return Math.min(totalApps, Math.max(base, index + 1));
}
